<!-- eslint-disable max-len -->
<template>
  <Stack class="completion-and-thanks">
    <Stack
      gap="4"
      items="center"
    >
      <img
        v-if="firmLogo"
        :src="firmLogo"
        :alt="firmName || 'Firm logo'"
        class="firm-logo"
        @error="firmLogoFailed = true"
      >

      <Title
        class="success-title"
        size="8"
      >
        Secure Document Portal
      </Title>
    </Stack>

    <Stack
      gap="2"
    >
      <p class="advisor-message">
        <template v-if="advisorName">
          <strong>{{ advisorName }}</strong>
          <template v-if="firmName">
            at {{ firmName }}
          </template>
        </template>
        <template v-else>
          Your financial advisor
        </template>
        has invited you to a secure document portal, where you can upload the documents they've requested for review.
      </p>

      <p class="guidance-text">
        <template v-if="hasPassword">
          Log in to see what they've requested.
        </template>
        <template v-else>
          To get started, set up your account with a password.
        </template>
      </p>
    </Stack>

    <!-- Auth Actions -->
    <Button
      v-if="hasPassword"
      variant="primary"
      size="5"
      @click="clientPortalStore.requestLoginRedirect()"
    >
      Log In to Access
    </Button>

    <Button
      v-else
      variant="primary"
      size="5"
      @click="clientPortalStore.handleRedirectToSignup()"
    >
      Set Up Your Account
    </Button>


    <div class="disclaimer">
      <Icon
        icon="fas fa-shield-alt"
        size="4"
      />
      <div class="item-body">
        <p>
          Your documents are securely stored and encrypted.
          <br>Only you and your advisor have access to them.
        </p>
      </div>
    </div>
  </Stack>
</template>

<script setup>
import { computed, ref, watch } from 'vue';
import { Stack, Button, Title, Icon } from '@stakeui';
import { useClientPortalStore } from '@frontend/ui/stores/clientPortalStore';

const clientPortalStore = useClientPortalStore();

const firmLogoFailed = ref(false);

const advisorName = computed(() => clientPortalStore.advisorInfo?.name || '');
const firmName = computed(() => clientPortalStore.firmInfo?.name || '');
const firmLogo = computed(() => (firmLogoFailed.value ? '' : clientPortalStore.firmInfo?.logo || ''));
const hasPassword = computed(() => Boolean(clientPortalStore.clientInfo?.user?.client_has_password));

watch(
    () => clientPortalStore.shouldRedirectLogin,
    should => {
      if (!should) return;

      clientPortalStore.clearLoginRedirect?.();

      const query = new URLSearchParams({
        entity: clientPortalStore.currentEntityId || '',
        advisor: clientPortalStore.advisorSlug || '',
        firm: clientPortalStore.firmSlug || '',
      }).toString();

      window.location.assign(`/account/client-login/?${query}`);
    },
);
</script>

<style scoped>
.completion-and-thanks {
  background-color: #fff;
  max-width: 50rem;
  margin: 0 auto;
  text-align: center;
  padding: 4em 3em;
  border-radius: 2em;
  box-shadow: 0px 32px 32px 0px rgba(0, 0, 0, 0.02), 0px 16px 16px 0px rgba(0, 0, 0, 0.02), 0px 8px 8px 0px rgba(0, 0, 0, 0.02), 0px 4px 4px 0px rgba(0, 0, 0, 0.02), 0px 2px 2px 0px rgba(0, 0, 0, 0.02), 0px 1px 1px 0px rgba(0, 0, 0, 0.02);
  justify-content: center;
}

.firm-logo {
  max-width: 14rem;
  max-height: 5rem;
  object-fit: contain;
}

.success-message {
  max-width: 20em;
  font-size: 1.25em;
}

.success-info {
  color: var(--stk-text-50);
}

.disclaimer {
  background-color: var(--stk-background);
  font-size: 0.875em;
  border-radius: 1em;
  padding: 1em 1.25em;
}
</style>
